import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Sidebar/sidebar";
import Details from "./Details/details";
import "./dataTransfer.css";
import { Button, Form, Tag, Card, Typography, Alert } from "antd";
import CardSection from "../../components/CardSection/card-section";
import InitiateTransfer from "./InitiateTransfer/initiate-transfer";
import Heading from "../../components/heading";
import { useNavigate, useLocation } from "react-router-dom";
import SupplierSignature from "./SupplierSignature/supplier-signature";
import RequesterSignature from "./RequesterSignature/requester-signature";
import UploadOfflineDTS from "./UploadOfflineDTS";
import {
  getViewPageChangeInfo,
  getUserIsid,
  formatRequesterName,
  eTMFWarningTextInitiatePage,
  offlineDTSText,
  shouldShowFlowToggle,
  resolveInitialOfflineFlow,
} from "../../components/Shared/utils";
import { getInitiateTransfer } from "../../services/initiate-transfer";
import ReviewUpload from "./ReviewUpload/review-upload";
import { SaveDraft } from "../../services/save-draft";
import { getDetails } from "../../services/get-details";
import DataUpload from "./data-upload";
import { useOpenNotification } from "../../components/Notification/notification";
import { getProtocolNumber } from "../../services/get-protocol-number";
import { fetchUserDetails } from "../../services/fetch-user-details";
import {
  filterSDTMDeliverables,
  updateDeliverablesWithDescription,
} from "./data-upload/utils";
import EditContact from "./EditContact/edit-contact";
import { InfoCircleOutlined, InfoCircleFilled } from "@ant-design/icons";
import GenericDialog from "../../components/GenericDialog/generic-dialog";
import { syncAdobeStatus } from "../../services/sync-adobe-status";
import { getUserEmail } from "../../services/utils";
import TransferContacts from "./Contact/transfer-contacts";
import { uploadAndProcessFile } from "./Details/data-format-file-upload";

type RequiredMark = boolean | "optional" | "customize";

export const customizeRequiredMark = (
  label: React.ReactNode,
  { required }: { required: boolean },
) => (
  <>
    {required ? (
      <Tag color="error">Required</Tag>
    ) : (
      <Tag color="warning">optional</Tag>
    )}
    {label}
  </>
);

const { Text } = Typography;

export interface Profile {
  email: string;
  family_name: string;
  given_name: string;
  profile: Profile;
  isid: string;
  sub: string;
}

interface DataType {
  key: string;
  requestername: string;
  requesteremail: string;
  requesterCompany?: string;
  requesterPhone?: string;
  requesterTitle?: string;
  requesterArea?: string;
}

type ProtocolData = {
  studyName: string;
  studyNumber: string;
};

interface FormValues {
  // protocol_number: any;
  DTSVersion?: string;
  MSDCompound?: string;
  msd_type?: string;
  supplierCompound?: string;
  supplierOrganization?: string;
  transferId?: string;
  requesters?: any; // Replace 'any' with the appropriate type
  suppliers?: any; // Replace 'any' with the appropriate type
  file_name?: string;
  record_id?: string;
  upload_dts?: any;
  created_by?: string;
  updated_by?: string;
  study_number?: string;
  msd?: string;
  protocol_number?: string;
  study_name?: string;
  status?: string;
  description_change?: string;
  purpose?: string;
  init_deliverables?: any;
  etmf_upload_flag: boolean;
  data_format: any;
  is_offline_flow?: boolean;
}

export interface RowData {
  key: string;
  compound: string;
  protocol_number: string;
  dts_version: string;
  transfer_id: string;
  modified: string;
  modified_date: string;
  action: { text: string; color: string }[];
}

export interface RowData {
  Agreement_ID: string | null;
  agreement_status: string;
  created_by: string | null;
  created_when: string;
  dts_version: string;
  file: string;
  msd: string;
  record_id: string; // This should match the key you are using
  requesters: any[]; // Assuming requesters is an array of Requester objects
  status: string;
  supplier_compound: string;
  supplier_org: string;
  suppliers: any[]; // Adjust this type as needed
  transfer_id: string;
  transient_documentID: string | null;
  updated_by: string | null;
  updated_when: string;
  rejection_reason: string;
  msd_type: string;
  description_change: string;
  purpose: string;
  init_deliverables: any;
  etmf_upload_flag: boolean;
  study_number: string;
  protocol_number: string;
  data_format: string;
  is_offline_flow?: boolean;
}
interface LocationState {
  rowData: RowData; // Define the expected structure of the row data
}

interface DataTransferProps {
  initialViewPage?: string;
}

const DataTransfer: React.FC<DataTransferProps> = ({ initialViewPage }) => {
  const [dataTransferFormRef] = Form.useForm();
  const [detailsFormValue, setDetailsFormValue] = useState<any>({});
  const [refFormValue, setRefFormValue] = useState<any>({});
  const [uploadDTSFormValue, setUploadDTSFormValue] = useState<any>();
  const [requiredMark] = useState<RequiredMark>(true);
  const [transferIdList, setTransferIdList] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isInitiateButtonDisabled, setIsInitiateButtonDisabled] =
    useState(false);
  const [isEditButtonDisabled, setIsEditButtonDisabled] = useState(false);
  const [requesterData, setRequesterData] = useState<DataType[]>([]);
  const [supplierData, setSupplierData] = useState<DataType[]>([]);
  const [success, setSuccess] = useState("");
  const [viewPage, setViewPage] = useState(initialViewPage ?? "data-transfer");
  const [suppErrorMessage, setSuppErrorMessage] = useState<string | null>(null); // State for error message
  const [reqErrorMessage, setReqErrorMessage] = useState<string | null>(null); // State for error message
  const [recordId, setRecordId] = useState("");
  const [nextSteps, setNextSteps] = useState(0);
  const [MSDCompoundOptionsValue, setMSDCompoundOptionsValue] = useState<any[]>(
    [],
  );
  const [msdDataLoading, setMsdDataLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [openETMFWarning, setOpenETMFWarning] = useState<boolean>(false);
  const [descriptionList, setDescriptionList] = useState<any[]>([]);
  const [sourceSystem, setSourceSystem] = useState<string>("");
  const [isOfflineFlow, setIsOfflineFlow] = useState<boolean>(false);
  const location = useLocation(); // Get the location object
  const rowData = (location?.state as LocationState)?.rowData;
  const [rowDetails] = useState<RowData>(rowData);
  const navigate = useNavigate();
  const { openNotification, contextHolder } = useOpenNotification();
  const dataTransferFormValues = Form.useWatch([], dataTransferFormRef);
  const [openCancelTransferWarning, setOpenCancelTransferWarning] =
    useState<boolean>(false);
  const [submitCancelTransfer, setSubmitCancelTransfer] =
    useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [isOfflineDtsUploadComplete, setIsOfflineDtsUploadComplete] =
    useState<boolean>(false);
  const [openSwitchSignatureWarning, setOpenSwitchSignatureWarning] =
    useState<boolean>(false);
  const [pendingOfflineFlow, setPendingOfflineFlow] = useState<boolean | null>(
    null,
  );
  const showFlowToggle = shouldShowFlowToggle(rowDetails?.status);

  useEffect(() => {
    if (viewPage === "data-transfer") {
      const hasMatchingCompound = MSDCompoundOptionsValue?.find((obj) => {
        return obj.studyName === dataTransferFormValues?.MSDCompound;
      });
      setSourceSystem(hasMatchingCompound?.sourceSystem);
    }
  }, [dataTransferFormValues, MSDCompoundOptionsValue, sourceSystem, viewPage]);

  useEffect(() => {
    if (viewPage !== "upload-offline-dts") {
      setIsOfflineDtsUploadComplete(false);
    }
  }, [viewPage]);

  // Helper function to render Form section
  const renderFormSection = (page: string) => (
    <Form
      form={dataTransferFormRef}
      layout="vertical"
      initialValues={{ remember: true }}
      requiredMark={
        requiredMark === "customize" ? customizeRequiredMark : requiredMark
      }
      scrollToFirstError={true}
    >
      {isOfflineFlow &&
        <Alert
          message={offlineDTSText()}
          type="info"
          showIcon
          icon={<InfoCircleFilled style={{ color: "#008576" }} />}
          style={{
            borderRadius: 0,
            borderColor: "#00855a24",
            background: "#00855a24",
            marginBottom: "10px",
          }}
        />
      }
      <Details
        optionsValue={transferIdList}
        msdValue={MSDCompoundOptionsValue}
        msdDataLoading={msdDataLoading}
        detailsSectionData={detailsFormValue}
        formRef={dataTransferFormRef}
        uploadDTSFormValue={uploadDTSFormValue}
        nextPage={viewPage}
        handleDeliverablesDescription={handleDeliverablesDescription}
        status={rowDetails?.status}
        isEditing={isEditing}
        showFlowToggle={showFlowToggle && viewPage === "data-transfer"}
        isOfflineFlow={isOfflineFlow}
        onFlowToggleChange={(nextFlowIsOffline: boolean) => {
          if (nextFlowIsOffline === isOfflineFlow) {
            return;
          }
          setPendingOfflineFlow(nextFlowIsOffline);
          setOpenSwitchSignatureWarning(true);
        }}
      />
      <CardSection
        id="contact-section"
        title="Contact Information"
        inputFields={getContactInputFiels()}
        subheader={isOfflineFlow ? "" : "When the transfer is initiated, your contacts will receive an Adobe Sign email requesting their signature on the DTS agreement. Only one contact from each group needs to sign."}
      />
      {success && <div className="msg">{success}</div>}
      <div className="save-next-button">
        <div className="save-next-botton-content"></div>
        <Card
          variant="borderless"
          title={page === "data-transfer" ? null : "Review"}
        >
          {page === "data-transfer" ? (
            <div>
              <Button
                className={`next-button ${isButtonDisabled ? "disabled" : "enabled"}`}
                onClick={handleSubmitNext}
                disabled={isButtonDisabled}
                type="primary"
                htmlType="submit"
              >
                {" "}
                Next{" "}
              </Button>
              {!isOfflineFlow &&
                <Button
                  style={{ marginRight: "20px" }}
                  onClick={handleSaveDraft}
                  loading={loading}
                >
                  {" "}
                  Save Draft{" "}
                </Button>}
              {(rowDetails
                && (rowDetails?.status !== "Cancelled" && rowDetails?.status !== "Cancel offline DTS transfer" && rowDetails?.status !== "Admin creates draft"))
                && (
                  <Button type="default" onClick={handleCancelTransferBtnClick}>
                    {" "}
                    Cancel Transfer{" "}
                  </Button>
                )}
            </div>
          ) : (
            <div>
              <div className="review-section">
                {(sourceSystem !== "VEEVA_CTMS" ||
                  dataTransferFormValues.MSDCompoundType === "OTHER") && (
                    <Alert
                      message={eTMFWarningTextInitiatePage()}
                      type="info"
                      showIcon
                      icon={<InfoCircleFilled style={{ color: "#008576" }} />}
                      style={{
                        borderRadius: 0,
                        borderColor: "#00855a24",
                        background: "#00855a24",
                        marginBottom: "10px",
                      }}
                    />
                  )}
                {isOfflineFlow ? (
                  <Text>
                    Once created, your DTS Agreement will be generated for the offline signatures.
                  </Text>
                ) : (
                  <Text>
                    Upon initiation of the transfer, your contacts will receive an
                    email from Adobe Sign requesting a signature on the DTS
                    Agreement. please verify that all information is correct
                    before proceeding.
                </Text>
                )}
              </div>
              <div>
                <Button
                  className="initiate-transfer"
                  type="primary"
                  onClick={handleInitiateTransfer}
                  loading={loading}
                  disabled={isInitiateButtonDisabled}
                  htmlType="submit"
                >
                  {" "}
                  Create Agreement{" "}
                </Button>
                <Button
                  className="edit-button"
                  disabled={isEditButtonDisabled}
                  onClick={handleTransferUpdate}
                >
                  {" "}
                  Edit{" "}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Form>
  );

  // Helper function to render main section based on viewPage
  const renderSection = (page: string) => {
    switch (page) {
      case "initiate-transfer":
        return (
          <div>
            <Card variant="borderless">
              <div id="initiate-transfer" className="initiate">
                <InitiateTransfer offlineFlow={isOfflineFlow} />
              </div>
            </Card>
          </div>
        );
      case "data-transfer":
      case "preview-page":
        return renderFormSection(page);
      case "supplier-signature":
        return (
          <div>
            <SupplierSignature
              supplierData={supplierData}
              requesterData={requesterData}
            />
          </div>
        );
      case "requester-signature":
        return (
          <div>
            <RequesterSignature
              requesterData={requesterData}
              supplierData={supplierData}
            />
          </div>
        );
      case "upload-offline-dts":
        return (
          <div>
            <UploadOfflineDTS
              rowDetails={rowDetails}
              onUploadComplete={() => setIsOfflineDtsUploadComplete(true)}
            />
          </div>
        );
      case "data-upload":
      case "finalize-transfer":
      case "reject-transfer":
        return (
          <div>
            <DataUpload
              transferIdOptions={transferIdList}
              data={rowDetails}
              currentPage={page}
            />
          </div>
        );
      case "review":
        return (
          <div>
            <ReviewUpload reviewData={rowDetails} recordId={recordId} />
          </div>
        );
      case "edit-contact":
        return (
          <div>
            <EditContact
              handleRequesterDataChange={handleRequesterDataChange}
              handleSupplierDataChange={handleSupplierDataChange}
              supplierData={supplierData}
              requesterData={requesterData}
              handleCloseContact={handleEditContact}
              rowData={rowDetails}
            />
          </div>
        );
      default:
        return (
          <div>
            <ReviewUpload />
          </div>
        );
    }
  };

  const fetchData = async () => {
    try {
      const response = await getDetails();
      if (response?.data) {
        const optionDetailsValue: any = response?.data;
        setTransferIdList(optionDetailsValue);
      }
    } catch (err) {
      openNotification("error", "An unexpected error occurred");
    }
  };

  const handleSubmitETMFWarning = () => {
    setOpenETMFWarning(false);
    setViewPage("preview-page");
  };

  const handleCancelETMFWarning = () => {
    setOpenETMFWarning(false);
  };

  const fetchProtocolData = async () => {
    try {
      setMsdDataLoading(true);
      const response = await getProtocolNumber();
      if (response?.data) {
        const sortedData = [...(response.data as ProtocolData[])].sort((a, b) =>
          a.studyName.localeCompare(b.studyName),
        );
        setMSDCompoundOptionsValue(sortedData);
        setMsdDataLoading(false);
      }
    } catch (err) {
      openNotification("error", "An unexpected error occurred");
      setMsdDataLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [viewPage]);

  useEffect(() => {
    fetchData();
    fetchProtocolData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formData = (getFormValue: any): FormValues | undefined => {
    const requestedEmail = getUserEmail() ?? undefined;
    // Split from the right at the last hyphen
    const splitValue = getFormValue?.MSDCompound.split("-");
    const protocol = splitValue.pop();
    const unblindingComponents = getFormValue?.unblindingComponents;
    const SDTMDeliverables = getFormValue?.SDTMDeliverables
      ? filterSDTMDeliverables(getFormValue?.SDTMDeliverables)
      : null;
    const deliverablesWithDesc = updateDeliverablesWithDescription(
      SDTMDeliverables,
      descriptionList,
    );
    const protectionHealthData = getFormValue?.protectionHealthData;
    const initDeliverables = {
      SDTMDeliverables: deliverablesWithDesc,
      unblindingComponents,
      protectionHealthData,
    };
    setUploadDTSFormValue(JSON.stringify(initDeliverables));
    const dataFormat = getFormValue?.dataFormat?.map((file: any) => ({ name: file.name }));
    const formValues: FormValues = {
      DTSVersion: getFormValue?.DTSVersion,
      MSDCompound: getFormValue?.MSDCompound,
      msd_type: getFormValue?.MSDCompoundType,
      study_name: getFormValue?.MSDCompound,
      supplierCompound:
        getFormValue?.supplierCompound === undefined ||
          getFormValue?.supplierCompound === ""
          ? getFormValue?.MSDCompound
          : getFormValue?.supplierCompound,
      supplierOrganization: getFormValue?.supplierOrganization,
      transferId: getFormValue?.transferId,
      description_change: getFormValue?.descriptionChange,
      purpose: getFormValue?.purpose,
      requesters: JSON.stringify(requesterData),
      suppliers: JSON.stringify(supplierData),
      init_deliverables: JSON.stringify(initDeliverables),
      record_id: recordId || "",
      etmf_upload_flag: sourceSystem === "VEEVA_CTMS",
      protocol_number: protocol,
      status: (viewPage === "data-transfer") ? "Save Draft" : (isOfflineFlow ? "Initiate Offline DTS transfer" : "Submitted"),
      data_format: dataFormat ?? [],
      is_offline_flow: isOfflineFlow,
    };
    if (!recordId) {
      formValues.created_by = requestedEmail;
    } else {
      formValues.updated_by = requestedEmail;
    }
    if (getFormValue?.MSDCompound) {
      const selectedStudyName = getFormValue.MSDCompound;
      const selectedItem = MSDCompoundOptionsValue.find(
        (item) => item.studyName === selectedStudyName,
      );
      const studyNumber = selectedItem ? selectedItem.studyNumber : null;
      formValues.study_number = studyNumber;
    }
    return formValues;
  };

  const handleEditContact = () => {
    if (viewPage === "edit-contact") {
      if (rowDetails.status === "Supplier Upload Submitted (PROD)") {
        setViewPage("review");
      } else {
        setViewPage("data-upload");
      }
    } else {
      setViewPage("edit-contact");
    }
  };

  const handleSaveDraft = async () => {
    try {
      await dataTransferFormRef.validateFields([
        "MSDCompoundType",
        "MSDCompound",
        "supplierOrganization",
        "DTSVersion",
        "transferId",
        "supplierCompound",
        "descriptionChange",
        "purpose",
        "dataFormat",
      ]);
      const saveDraftFormValues = dataTransferFormRef.getFieldsValue();
      const getFormValues = formData(saveDraftFormValues);
      setLoading(true);
      const isFileUpload = await uploadAndProcessFile(
        saveDraftFormValues?.dataFormat,
        saveDraftFormValues,
        Array.isArray(saveDraftFormValues?.dataFormat) && saveDraftFormValues?.dataFormat.every((file: any) => file.originFileObj),
        true,
        false,
      );
      if (isFileUpload) {
        await SaveDraft(getFormValues)
          .then((response) => {
            const recordInfo = response?.data?.record_id;
            if (recordInfo) {
              setRecordId(recordInfo);
            }
            openNotification("success", "Draft is saved successfully!");
            setLoading(false);
          }).catch((error) => {
            openNotification(
              "error",
              error?.message ? error?.message : "Unknown error",
            );
            setLoading(false);
          });
      } else {
        openNotification("error", "Upload incomplete: no valid uploaded parts found. File may have been removed/quarantined locally. Please re-upload.");
        setLoading(false);
      }
      setSuccess("");
    } catch (err: any) {
      return;
    }
  };

  const handleDeliverablesDescription = (data: any) => {
    setDescriptionList(data);
  };

  const handleSubmitNext = async () => {
    try {
      if (requesterData.length === 0 || supplierData.length === 0) {
        if (requesterData.length === 0) {
          setReqErrorMessage("This is a required field.");
        } else if (supplierData.length === 0) {
          setSuppErrorMessage("This is a required field.");
        }
        return;
      }
      const detailsFormValues = await dataTransferFormRef.validateFields();
      const saveDraftFormValues = dataTransferFormRef.getFieldsValue();
      setRefFormValue(saveDraftFormValues);
      setDetailsFormValue({
        DTSVersion: detailsFormValues?.DTSVersion,
        MSDCompound: detailsFormValues?.MSDCompound,
        supplierCompound:
          detailsFormValues.supplierCompound === undefined ||
            detailsFormValues.supplierCompound === ""
            ? detailsFormValues.MSDCompound
            : detailsFormValues.supplierCompound,
        supplierOrganization: detailsFormValues?.supplierOrganization,
        transferId: detailsFormValues?.transferId,
        MSDCompoundType: detailsFormValues?.MSDCompoundType,
        descriptionChange: detailsFormValues?.descriptionChange,
        purpose: detailsFormValues?.purpose,
        dataFormat: detailsFormValues?.dataFormat,
      });
      if (
        sourceSystem !== "VEEVA_CTMS" ||
        detailsFormValues.MSDCompoundType === "OTHER"
      ) {
        setOpenETMFWarning(true);
      } else {
        setViewPage("preview-page");
      }
    } catch (err: any) {
      return;
    }
  };

  const handleTransferUpdate = () => {
    setIsInitiateButtonDisabled(false);
    setIsEditing(true);
    setViewPage("data-transfer");
  };

  const handleInitiateTransfer = async () => {
    const getFormValues = formData(refFormValue);
    if (!getFormValues) return;
    setLoading(true);
    setIsEditButtonDisabled(true);
    const isDTSUpload = await uploadAndProcessFile(
      detailsFormValue?.dataFormat,
      getFormValues,
      Array.isArray(detailsFormValue?.dataFormat) && detailsFormValue?.dataFormat.every((file: any) => file.originFileObj),
      true,
      false,
    );
    try {
      if (isDTSUpload) {
        const response = await SaveDraft(getFormValues);
        const getRecordId = response?.data?.record_id;
        getRecordId && setRecordId(getRecordId);
        if (getRecordId && !isOfflineFlow) {
          await handleProfileAndTransfer(getRecordId);
        } else {
          setViewPage("initiate-transfer");
        }
        setIsInitiateButtonDisabled(true);
      } else {
        openNotification("error", "Upload incomplete: no valid uploaded parts found. File may have been removed/quarantined locally. Please re-upload.");
        setLoading(false);
        setIsEditButtonDisabled(false);
      }
    } catch (error: any) {
      setIsEditButtonDisabled(false);
      setLoading(false);
      const errorMessage = error.message || "An unexpected error occurred";
      openNotification("error", errorMessage);
    }
  };

  const handleProfileAndTransfer = async (getRecordId: any) => {
    const requestedEmail = getUserEmail() ?? null;

    try {
      const getInitiateTransferResponse = await getInitiateTransfer(
        getRecordId,
        requestedEmail,
      );
      if (getInitiateTransferResponse?.data) {
        setViewPage("initiate-transfer");
        setNextSteps(1);
        setIsInitiateButtonDisabled(false);
      } else {
        setIsInitiateButtonDisabled(false);
        setIsEditButtonDisabled(false);
        setLoading(false);
        const resError = getInitiateTransferResponse?.error ?? "Unknown error";
        openNotification(
          "error",
          typeof resError === "string" ? resError : JSON.stringify(resError),
        );
      }
      // Handle the response as needed
    } catch (err: any) {
      setIsEditButtonDisabled(false);
      openNotification("error", "An unexpected error occurred");
    }
  };

  // Callback function to update requester data
  const handleRequesterDataChange = (newData: any) => {
    setRequesterData(newData);
    setReqErrorMessage(null);
  };

  const handleSupplierDataChange = (newData: any) => {
    setSupplierData(newData);
    setSuppErrorMessage(null);
  };

  const getContactInputFiels = () => {
    return (
      <div id="contact-table">
        <TransferContacts
          onDataChange={handleRequesterDataChange}
          nextPage={viewPage}
          contactData={requesterData}
          errorMessage={reqErrorMessage}
          contactType="requester"
        />
        <TransferContacts
          onDataChange={handleSupplierDataChange}
          nextPage={viewPage}
          contactData={supplierData}
          errorMessage={suppErrorMessage}
          contactType="supplier"
        />
      </div>
    );
  };

  const requesterDetails = async (userIsid: string) => {
    try {
      const response = await fetchUserDetails(userIsid);
      return response;
    } catch (err) {
      openNotification("error", "failed to fetching user details.");
      return null;
    }
  };

  const handleCancelTransferBtnClick = async () => {
    if (rowDetails?.status === "Submitted" || rowDetails?.status === "Approve Agreement") {
      try {
        setIsSyncing(true);
        const currentDTSStatus = await syncAdobeStatus(rowDetails.record_id);

        if (currentDTSStatus === null) {
          throw new Error("Failed to get user profile");
        } else if (currentDTSStatus === "Finalize Agreement") {
          openNotification("info", "Cancel prohibited. DTS already signed.");
          setIsSyncing(false);

          setTimeout(() => {
            navigate("/dashboard");
          }, 1500);
          return;
        }

        setIsSyncing(false);
      } catch (error) {
        openNotification("error", "An unexpected error occurred");
        setIsSyncing(false);
        return;
      }
    }

    setOpenCancelTransferWarning(true);
  };

  const handleCloseCancelTransferWarning = () => {
    setOpenCancelTransferWarning(false);
  };

  const handleCloseSwitchSignatureWarning = () => {
    dataTransferFormRef?.setFieldsValue({
      signatureMethod: isOfflineFlow ? "offline" : "online",
    });
    setOpenSwitchSignatureWarning(false);
    setPendingOfflineFlow(null);
  };

  const handleSubmitSwitchSignatureWarning = () => {
    if (typeof pendingOfflineFlow === "boolean") {
      setIsOfflineFlow(pendingOfflineFlow);
      dataTransferFormRef?.setFieldsValue({
        signatureMethod: pendingOfflineFlow ? "offline" : "online",
      });
    }
    setOpenSwitchSignatureWarning(false);
    setPendingOfflineFlow(null);
  };

  const handleSubmitCancelTransferWarning = async () => {
    const formValues = {
      DTSVersion: rowDetails.dts_version,
      MSDCompound: rowDetails.msd,
      supplierOrganization: rowDetails.supplier_org,
      transferId: rowDetails.transfer_id,
      record_id: rowDetails.record_id,
      status: isOfflineFlow ? "Cancel offline DTS transfer" : "Cancelled",
      updated_by: getUserEmail() ?? undefined,
      is_offline_flow: isOfflineFlow,
    };
    setSubmitCancelTransfer(true);
    const saveDraftResponse = await SaveDraft(formValues);
    setSubmitCancelTransfer(false);
    if (saveDraftResponse?.data) {
      setOpenCancelTransferWarning(false);
      openNotification("success", "This transfer has been cancelled.");
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
    } else {
      setOpenCancelTransferWarning(false);
      openNotification("error", "An unexpected error occurred");
    }
  };

  useEffect(() => {
    (async () => {
      const authDataString = sessionStorage?.getItem("profile");
      const isEditMode = rowDetails && Object.keys(rowDetails).length > 0;
      setIsOfflineFlow(
        resolveInitialOfflineFlow(rowDetails?.status, rowDetails?.is_offline_flow),
      );

      if (isEditMode) {
        setRecordId(rowDetails?.record_id);
        setRequesterData(rowDetails?.requesters);
        setSupplierData(rowDetails?.suppliers);
        setIsButtonDisabled(false);
        setDetailsFormValue({
          DTSVersion: rowDetails?.dts_version,
          MSDCompound: rowDetails?.msd,
          supplierCompound: rowDetails?.supplier_compound,
          supplierOrganization: rowDetails?.supplier_org,
          transferId: rowDetails?.transfer_id,
          MSDCompoundType: rowDetails?.msd_type,
          descriptionChange: rowDetails?.description_change,
          purpose: rowDetails?.purpose,
          dataFormat: rowDetails?.data_format,
        });
        setUploadDTSFormValue(rowDetails?.init_deliverables);
        const { currentAction } = getViewPageChangeInfo(rowDetails?.status);
        setViewPage(currentAction);
      } else if (authDataString) {
        const userIsid = getUserIsid();
        if (userIsid) {
          const userData = await requesterDetails(userIsid);
          setRequesterData([
            {
              key: "0",
              requestername: formatRequesterName(userData.displayName),
              requesteremail: userData?.email || "",
              requesterCompany: userData?.company || "",
              requesterPhone: userData?.phone || "",
              requesterTitle: userData?.title || "",
              requesterArea: userData?.department || "",
            },
          ]);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowDetails]);

  //  // Update nextSteps based on viewPage
  useEffect(() => {
    if (isOfflineFlow) {
      switch (viewPage) {
        case "data-transfer":
          setNextSteps(0);
          break;
        case "upload-offline-dts":
          setNextSteps(1);
          break;
        case "data-upload":
        case "reject-transfer":
        case "finalize-transfer":
          setNextSteps(2);
          break;
        case "review":
          setNextSteps(3);
          break;
        default:
          setNextSteps(0);
          break;
      }
    } else {
      switch (viewPage) {
        case "data-transfer":
          setNextSteps(0);
          break;
        case "supplier-signature":
          setNextSteps(1);
          break;
        case "requester-signature":
          setNextSteps(2);
          break;
        case "data-upload":
        case "reject-transfer":
        case "finalize-transfer":
          setNextSteps(3);
          break;
        case "review":
          setNextSteps(4);
          break;
        default:
          setNextSteps(0);
          break;
      }
    }
  }, [viewPage, isOfflineFlow]);

  return (
    <div>
      <Heading
        title="Transfer"
        onClickNavigate={handleCancelTransferBtnClick}
        id="transfer-btn"
        userDetails={rowDetails}
        currentPage={viewPage}
        handleEditContact={handleEditContact}
        buttonName={rowDetails && "Cancel Transfer"}
        btnLoading={isSyncing}
        hideCancelButton={
          isOfflineDtsUploadComplete && viewPage === "upload-offline-dts"
        }
      />

      <div className="data-transfer-section">
        {viewPage !== "edit-contact" && (
          <div className="section-left">
            <Sidebar nextSteps={nextSteps} offlineFlow={isOfflineFlow} />
          </div>
        )}
        <div className="section-right">{renderSection(viewPage)}</div>
      </div>

      <GenericDialog
        open={openETMFWarning}
        submitButtonText="Confirm to Review"
        cancelButtonText="Cancel"
        handleSubmit={handleSubmitETMFWarning}
        handleCancel={handleCancelETMFWarning}
        width={450}
        title="Continue"
        titleIcon={
          <InfoCircleOutlined
            style={{ color: "orange", marginRight: "15px" }}
          />
        }
        description="The MSD Compound/Protocol that you selected does not have a folder existing in eTMF. If you continue with initiating the transfer, you will be responsible for uploading the signed DTS .pdf file into the eTMF manually."
      />

      <GenericDialog
        open={openCancelTransferWarning}
        submitButtonText="Yes"
        cancelButtonText="No"
        handleSubmit={handleSubmitCancelTransferWarning}
        handleCancel={handleCloseCancelTransferWarning}
        okButtonLoading={submitCancelTransfer}
        width={450}
        title="Cancel Transfer"
        titleIcon={
          <InfoCircleOutlined
            style={{ color: "orange", marginRight: "15px" }}
          />
        }
        description="Are you sure you want to cancel this transfer?"
      />

      <GenericDialog
        open={openSwitchSignatureWarning}
        submitButtonText="Switch Signature Method"
        cancelButtonText="Back"
        handleSubmit={handleSubmitSwitchSignatureWarning}
        handleCancel={handleCloseSwitchSignatureWarning}
        width={560}
        title="Switch Signature Method?"
        titleIcon={
          <InfoCircleOutlined
            style={{ color: "orange", marginRight: "15px" }}
          />
        }
        description={
          <>
            <p>
              Are you sure you want to switch the signature method for this DTS
              agreement?
            </p>
            <p>
              Requesters must resume agreement creation for this transfer ID
              before participants can sign.
            </p>
          </>
        }
      />
      {contextHolder}
    </div>
  );
};

export default DataTransfer;
